import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { showLeadershipCardOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { TotalRevenueChart } from '../../CompanyProfiles/Performance/components/Charts/TotalRevenueChart';
import { selectedCompanyIdProfileAtom } from '../../CompanyProfiles/state/UIStateJ';
import { FinancingHistoryTable } from '../../CompanyProfiles/Summary/components/FinancingHistory/FinancingHistoryTable';
import { NewsSection } from '../../CompanyProfiles/Summary/components/NewsSection';
import { CompanyDetailsPanel } from './CompanyDetailsPanel';
import { DigestPanel } from './DigestPanel';
import { InvestmentMetricsPanel } from './InvestmentMetricsPanel';
import { LeadershipPanel } from './LeadershipPanel';
import { OwnershipTimelinePanel } from './OwnershipTimeline/OwnershipTimelinePanel';
import { Panel } from './Panel';
import { RecentActivityPanel } from './RecentActivity/RecentActivityPanel';
import { CompanyProfilesContainerBreakpoint } from './summary-layout.styles';

const MainWrapper = styled('div')`
  container-name: company-profiles-container;
  container-type: inline-size;
  display: grid;
  height: 100%;
  overflow: auto;
  width: 100%;
`;

const SummaryLayout = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showOwnership' && prop !== 'showLeadership',
})<{
  showOwnership: boolean;
  showLeadership: boolean;
}>`
  display: grid;
  gap: 1rem;
  height: 100%;
  padding: 1rem;
  width: 100%;

  @container company-profiles-container (width < ${CompanyProfilesContainerBreakpoint}) {
    grid-template-columns: 1fr 0fr;
    grid-template-areas:
      'digest'
      'digest'
      'details'
      'details'
      ${({ showLeadership }) => (showLeadership ? "'leader' 'leader' 'leader'" : '')}
      'ttl-rev'
      'invst-mtr'
      'invst-mtr'
      'activity'
      'activity'
      ${({ showOwnership }) => (showOwnership ? "'ownership' 'ownership'" : '')}
      'fin-hist'
      'fin-hist'
      'news'
      'news';
  }

  @container company-profiles-container (width >= ${CompanyProfilesContainerBreakpoint}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'digest details'
      'digest details'
      ${({ showLeadership }) =>
        showLeadership ? "'ttl-rev leader' 'ttl-rev leader'" : "'ttl-rev ttl-rev' 'ttl-rev ttl-rev'"}
      'invst-mtr activity'
      'invst-mtr activity'
      ${({ showOwnership }) => (showOwnership ? "'ownership ownership' 'ownership ownership'" : '')}
      'fin-hist fin-hist'
      'fin-hist fin-hist'
      'news news'
      'news news';
  }
`;

const PanelWrapper = styled('div')`
  height: 100%;
  width: 100%;
  min-height: 160px;
`;

export function CompanySummaryPortfolio() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const { useShowOwnershipTimeline } = useFlags<MaggieFeatureFlags>();
  const showLeadership = useAtomValue(showLeadershipCardOrgState);

  return (
    <MainWrapper>
      <SummaryLayout showOwnership={useShowOwnershipTimeline} showLeadership={showLeadership}>
        <PanelWrapper style={{ gridArea: 'digest' }}>
          <DigestPanel companyId={companyId} />
        </PanelWrapper>
        <PanelWrapper style={{ gridArea: 'details' }}>
          <CompanyDetailsPanel companyId={companyId} />
        </PanelWrapper>
        {showLeadership && (
          <PanelWrapper style={{ gridArea: 'leader' }}>
            <GenericFallbacksWrapper>
              <LeadershipPanel companyId={companyId} />
            </GenericFallbacksWrapper>
          </PanelWrapper>
        )}
        <PanelWrapper style={{ gridArea: 'ttl-rev' }}>
          <TotalRevenueChart />
        </PanelWrapper>
        <PanelWrapper style={{ gridArea: 'invst-mtr' }}>
          <InvestmentMetricsPanel companyId={companyId} />
        </PanelWrapper>
        <PanelWrapper style={{ gridArea: 'activity' }}>
          <RecentActivityPanel />
        </PanelWrapper>
        {useShowOwnershipTimeline && (
          <PanelWrapper style={{ gridArea: 'ownership' }}>
            <OwnershipTimelinePanel companyId={companyId} />
          </PanelWrapper>
        )}
        <PanelWrapper style={{ gridArea: 'fin-hist' }}>
          <Panel title={'Financing History'} collapsible defaultExpanded>
            <FinancingHistoryTable />
          </Panel>
        </PanelWrapper>
        <PanelWrapper style={{ gridArea: 'news' }}>
          <Panel title={'News'}>
            <NewsSection />
          </Panel>
        </PanelWrapper>
      </SummaryLayout>
    </MainWrapper>
  );
}
